import { Divider, List, Paper, Stack } from '@mui/material';
import { Fragment, useState } from 'react';

import { InternalLink, Text, type api, colors, isDefined } from 'common';

import { AdditionalKnowledgeBaseFileUploadDropzone } from './AdditionalKnowledgeBaseFileUploadDropzone';
import { DefaultKnowledgeBaseFileDropzone } from './DefaultKnowledgeBaseFileDropzone';
import { FileReferenceDialog } from './FileReferenceDialog';
import { FileReferenceSelected } from './FileReferenceSelected';
import { FileSelectDialog } from './FileSelectDialog';
import { KnowledgeBaseFileSelected } from './KnowledgeBaseFileSelected';
import { KnowledgeBaseUploadFileDialog } from '../../../../../../../KnowledgeBase/UploadFileDialog';
import { UploadFileProvider } from '../../../../../../../KnowledgeBase/fileUploadContext';
import {
  convertToFileReference,
  convertToKnowledgeBaseFileIdOrUndefined,
  isKnowledgeBaseFile,
} from '../../../../../../../KnowledgeBase/utils';
import { useFlowDefinitionContext } from '../../../../../context';

interface MultipleFileReferencesFieldProps {
  fileReferences: api.FlowDefinition.LlmPromptBlock['config']['fileReferences'];
  setFileReferences: (files: api.FlowDefinition.LlmPromptBlock['config']['fileReferences']) => void;
}

export function MultipleFileReferencesField({
  fileReferences,
  setFileReferences,
}: MultipleFileReferencesFieldProps) {
  const {
    flowDefinition: { organizationId },
  } = useFlowDefinitionContext();

  const selectedKnowledgeBaseFileIds = (fileReferences ?? [])
    .map((fileReference) => convertToKnowledgeBaseFileIdOrUndefined({ fileReference }))
    .filter(isDefined);

  const setSelectedKnowledgeBaseFileIds = (knowledgeBaseFileIds?: string[]) => {
    if (!isDefined(knowledgeBaseFileIds) || knowledgeBaseFileIds.length === 0) {
      setFileReferences(undefined);
      return;
    }
    const knowledgeBaseFileReferences = knowledgeBaseFileIds.map((id) =>
      convertToFileReference({ knowledgeBaseFileId: id })
    );
    const nonKnowledgeBaseFileReferences = (fileReferences ?? []).filter(
      (fileReference) => !isKnowledgeBaseFile({ fileReference })
    );
    setFileReferences([...knowledgeBaseFileReferences, ...nonKnowledgeBaseFileReferences]);
  };
  const [isBrowseExistingFilesDialogOpen, setIsBrowseExistingFilesDialogOpen] = useState(false);
  const [openFileReferenceIndex, setOpenFileReferenceIndex] = useState<number | undefined>(
    undefined
  );
  return (
    <UploadFileProvider
      flowOrganizationId={organizationId}
      onFileUploaded={async (knowledgeBaseFile) => {
        setSelectedKnowledgeBaseFileIds([...selectedKnowledgeBaseFileIds, knowledgeBaseFile.id]);
      }}
    >
      <Stack gap={2}>
        <Text variant="heading5" bold>
          Reference Files
        </Text>
        {(!isDefined(fileReferences) || fileReferences?.length === 0) && (
          <DefaultKnowledgeBaseFileDropzone
            onBrowseFiles={() => setIsBrowseExistingFilesDialogOpen(true)}
          />
        )}
        {fileReferences && fileReferences.length > 0 && (
          <Paper variant="outlined">
            <List disablePadding>
              {fileReferences?.map((fileReference, index) => {
                const fileId =
                  convertToKnowledgeBaseFileIdOrUndefined({ fileReference }) ?? fileReference;
                return (
                  <Fragment key={fileId}>
                    {index !== 0 && <Divider />}
                    {isKnowledgeBaseFile({ fileReference }) ? (
                      <KnowledgeBaseFileSelected
                        knowledgeBaseFileId={fileId}
                        onRemove={() => {
                          setFileReferences(
                            fileReferences.filter(
                              (fileReferenceForFilter) => fileReferenceForFilter !== fileReference
                            )
                          );
                        }}
                      />
                    ) : (
                      <FileReferenceSelected
                        fileReference={fileReference}
                        onRemove={() => {
                          setFileReferences(
                            fileReferences.filter(
                              (fileReferenceForFilter) => fileReferenceForFilter !== fileReference
                            )
                          );
                        }}
                      />
                    )}
                  </Fragment>
                );
              })}
            </List>
          </Paper>
        )}
        {isDefined(fileReferences) && fileReferences.length > 0 && (
          <AdditionalKnowledgeBaseFileUploadDropzone
            onBrowseFiles={() => setIsBrowseExistingFilesDialogOpen(true)}
          />
        )}
        <Text color={colors.extended.gray['45']} variant="body2">
          Upload a File from <InternalLink to="/knowledge-base">Knowledge Base</InternalLink> or
          provide a File Reference ID for context
        </Text>
      </Stack>
      {isBrowseExistingFilesDialogOpen && (
        <FileSelectDialog
          onClose={() => setIsBrowseExistingFilesDialogOpen(false)}
          selectedKnowledgeBaseFileIds={selectedKnowledgeBaseFileIds}
          setSelectedKnowledgeBaseFileIds={setSelectedKnowledgeBaseFileIds}
          onClickUseFileId={() => setOpenFileReferenceIndex(fileReferences?.length ?? 0)}
        />
      )}
      <FileReferenceDialog
        onClose={() => setOpenFileReferenceIndex(undefined)}
        fileReferencesIndex={openFileReferenceIndex}
      />
      <KnowledgeBaseUploadFileDialog />
    </UploadFileProvider>
  );
}
